import { first, get, isEmpty, isNil, set } from 'lodash';
import {
  ProductVariationFlavor,
  ProductVariationSize,
} from 'src/components/accordion';
import { GCFProductsContextProps } from 'src/components/context/Context.interface';
import {
  type ICCAsset,
  type IGcfProductListProduct,
  type IGcfProductListProductImage,
  type IGcfProductListProductSize,
  type IGcfProductListProductSizes,
  type IGcfProductListProductSizeVariation,
  type IGcfProductListProductSizeVariations,
  IProduct,
  IProductVariation,
} from 'src/interfaces';
import { sortItemsByOrder } from 'src/utils';
import { getProductNameFromAssetId } from 'src/utils/gcf-helper';

export const getProductsOptions = (
  products: IProduct[],
  assets: ICCAsset[],
) => {
  if (isEmpty(products)) return undefined;

  const options = products.reduce(
    (acc: { label: string; value: string }[], product) => {
      const variations = product?.productVariations ?? [];
      const productVariation = variations.find(
        (variation) => variation.product_code === product?.product_code,
      );

      const option = {
        label: `${product.product_code} - ${
          product.name_override ||
          productVariation?.name ||
          getProductNameFromAssetId(assets, first(product.asset_id)) ||
          `Product #${product?.product_code}`
        }`,
        value: product.product_code,
      };
      acc.push(option);

      return acc;
    },
    [],
  );

  return options;
};

export const formatProduct = (
  product?: IProduct,
  updateField?: { active: boolean },
) => {
  if (!product) return undefined;

  return { ...product, ...updateField } as IProduct;
};

export const formatProductVariationSizesWithFlavors = (
  productVariations: IProductVariation[] = [],
  sizes?: IGcfProductListProductSizes,
) => {
  const newProductVariations = productVariations
    .reduce((acc: ProductVariationSize[], productVariation) => {
      const foundProductVariationSize = acc.find(
        (item) => item.id === productVariation.product_variation_size_id,
      );

      if (foundProductVariationSize) {
        return acc.map((item) =>
          item.id === productVariation.product_variation_size_id
            ? updateProductVariationSize(item, productVariation, sizes)
            : item,
        );
      } else {
        return [
          ...acc,
          createProductVariationSize(productVariation, acc.length, sizes),
        ];
      }
    }, [])
    .sort(sortSizesByOrder);

  return newProductVariations;
};

export const transformProductVariationSizesWithFlavors = (
  productVariationSizes: ProductVariationSize[],
) => {
  const newSizes = productVariationSizes
    .sort(sortSizesByOrder)
    .map((item, index) => ({ ...item, order: index + 1 }))
    .reduce((acc, productVariationSize) => {
      if (productVariationSize.flavors.every((flavor) => !flavor.order)) {
        return acc;
      }

      return {
        ...acc,
        [productVariationSize.name]: mapProductSizesToGCF(productVariationSize),
      };
    }, {} as IGcfProductListProductSizes);

  return newSizes;
};

export const mapProductSizesToGCF = (
  productVariationSize: ProductVariationSize,
) => {
  const variations = transformFlavors(productVariationSize.flavors);

  return {
    order: productVariationSize.order,
    name: productVariationSize.name,
    variations,
    config: {
      showLabel: productVariationSize.showLabel,
      showPrice: productVariationSize.showPrice,
      showCalories: productVariationSize.showCalories,
      showFlavors: productVariationSize.showFlavors,
    },
  } as IGcfProductListProductSize;
};

type ReturnDefaultAssets =
  | {
      defaultSize?: string;
      defaultFlavor?: string;
      data?: IGcfProductListProductImage[];
    }
  | undefined;

export const getDefaultAssetFromGCF = (
  sizes?: IGcfProductListProductSizes,
  defaultSize?: string,
): ReturnDefaultAssets => {
  if (!isNil(defaultSize) && sizes?.[defaultSize]) {
    const sizeValue = sizes[defaultSize];
    const { data, defaultFlavor } = findVisibleImageOrFirstFlavorImages(
      sizeValue.variations,
    );

    return {
      defaultSize: defaultSize,
      defaultFlavor: defaultFlavor,
      data: data,
    };
  } else {
    const sizesEntries = Object.entries(sizes || {});

    for (const [index, [sizeKey, sizeValue]] of sizesEntries.entries()) {
      const { data: images, defaultFlavor } =
        findVisibleImageOrFirstFlavorImages(sizeValue.variations);

      const imageAvailable =
        images?.length || index === sizesEntries?.length - 1;
      if (imageAvailable) {
        return {
          defaultSize: sizeKey,
          defaultFlavor: defaultFlavor,
          data: images,
        };
      }
    }
  }
};

export const updateDefaultAssetToGCF = (
  sizes?: IGcfProductListProductSizes,
  asset?: IGcfProductListProductImage | IGcfProductListProductImage[],
  defaultSize?: string,
) => {
  const updatedSizes = { ...sizes };

  const defaultAssetsData = getDefaultAssetFromGCF(updatedSizes, defaultSize);

  if (defaultAssetsData) {
    set(
      updatedSizes,
      [
        defaultAssetsData?.defaultSize ?? '',
        'variations',
        defaultAssetsData?.defaultFlavor ?? '',
        'config',
        'images',
      ],
      Array.isArray(asset) ? asset : [asset],
    );
  }

  return updatedSizes;
};

const findVisibleImageOrFirstFlavorImages = (
  flavors: IGcfProductListProductSizeVariations,
) => {
  let firstFlavorImages = undefined;
  let defaultFlavor = undefined;

  for (const [flavorKey, flavorValue] of Object.entries(flavors)) {
    if (!firstFlavorImages || flavorValue.order === 1) {
      firstFlavorImages = flavorValue.config.images;
      defaultFlavor = flavorKey;
    }

    const visibleImage = flavorValue.config.images?.find(
      (image) => image.visible,
    );
    if (visibleImage) {
      return {
        defaultFlavor: flavorKey,
        data: flavorValue.config.images,
      };
    }
  }

  return {
    defaultFlavor,
    data: firstFlavorImages,
  };
};

const getFlavorAsset = (
  productVariation: IProductVariation,
  sizes?: IGcfProductListProductSizes,
): IGcfProductListProductImage[] => {
  const images = get(
    sizes,
    [
      productVariation.productVariationSize?.name ?? '',
      'variations',
      productVariation.product_code,
      'config',
      'images',
    ],
    [],
  );

  return images;
};

const updateProductVariationSize = (
  productVariationSize: ProductVariationSize,
  productVariation: IProductVariation,
  sizes?: IGcfProductListProductSizes,
) => {
  const newFlavor = createProductVariationFlavor(productVariation, sizes);

  return {
    ...productVariationSize,
    flavors: [...productVariationSize.flavors, newFlavor].sort(
      sortItemsByOrder,
    ),
  } as ProductVariationSize;
};

const createProductVariationSize = (
  productVariation: IProductVariation,
  sizeIndex: number,
  sizes?: IGcfProductListProductSizes,
) => {
  const sizeName = get(productVariation, ['productVariationSize', 'name']);

  return {
    ...productVariation.productVariationSize,
    flavors: [createProductVariationFlavor(productVariation, sizes)],
    order: get(sizes, [sizeName, 'order'], sizeIndex + 1),
    showCalories: get(sizes, [sizeName, 'config', 'showCalories'], true),
    showLabel: get(sizes, [sizeName, 'config', 'showLabel'], true),
    showPrice: get(sizes, [sizeName, 'config', 'showPrice'], true),
    showFlavors: get(sizes, [sizeName, 'config', 'showFlavors'], true),
  } as ProductVariationSize;
};

const createProductVariationFlavor = (
  productVariation: IProductVariation,
  sizes?: IGcfProductListProductSizes,
) => {
  const sizeName = get(productVariation, ['productVariationSize', 'name']);

  return {
    ...productVariation.productVariationFlavor,
    order: get(sizes, [
      sizeName,
      'variations',
      productVariation.product_code,
      'order',
    ]),
    productCode: productVariation.product_code,
    images: getFlavorAsset(productVariation, sizes),
  } as ProductVariationFlavor;
};

const transformFlavors = (flavors: ProductVariationFlavor[]) => {
  return flavors.reduce((acc, flavor) => {
    if (flavor.order) {
      return {
        ...acc,
        [flavor.productCode]: transformFlavor(flavor),
      };
    }

    return acc;
  }, {} as IGcfProductListProductSizeVariations);
};

const transformFlavor = (flavor: ProductVariationFlavor) => {
  return {
    order: flavor.order,
    productCode: flavor.productCode,
    config: {
      images: flavor.images,
    },
  } as IGcfProductListProductSizeVariation;
};

export const sortSizesByOrder = <
  T extends { flavors: { order?: number }[]; order: number },
>(
  a: T,
  b: T,
) => {
  if (a.flavors.every((item) => !item.order)) return 1;
  if (b.flavors.every((item) => !item.order)) return -1;

  return a.order - b.order;
};

export const updateImageVisibility = (
  product: IGcfProductListProduct,
  defaultSize?: string,
) => {
  Object.entries(product.sizes).forEach(([sizeKey, size]) => {
    Object.values(size.variations).forEach((variation) => {
      const visible = sizeKey === defaultSize && variation.order === 1;

      if (isEmpty(variation?.config?.images)) {
        if (visible) {
          set(variation, 'config.images', [{ visible }]);
        }
      } else {
        variation.config.images!!.forEach((image) => {
          image.visible = visible;
        });
      }
    });
  });
};

export const getProductImages = (
  product?: IProduct,
  defaultFlavor?: string,
  themeId?: number,
) => {
  const assets =
    product?.productVariations.reduce((acc, item) => {
      if (item.product_code === defaultFlavor) {
        const filteredAssets = themeId
          ? item.assets?.filter((asset) => asset.theme_id.includes(themeId)) ??
            []
          : item.assets?.filter((asset) => isEmpty(asset.theme_id)) ?? [];

        return acc.concat(filteredAssets);
      }

      return acc;
    }, [] as ICCAsset[]) ?? [];

  return !isEmpty(assets) ? assets : product?.assets ?? [];
};

export const generateSizeOptions = () => {
  return Array.from({ length: 150 }, (_, i) => `${i}px`);
};

export const getSupportingProductVariations = (
  products: IProduct[],
  productVariationCode?: string,
  gcfProducts?: GCFProductsContextProps['value'],
) => {
  let supportingProduct = products.find((item) =>
    item.productVariations.some(
      (variation) => variation.product_code === productVariationCode,
    ),
  );

  if (
    supportingProduct?.product_code &&
    gcfProducts &&
    gcfProducts[supportingProduct.product_code]
  ) {
    supportingProduct = formatProduct(
      products.find(
        (item) => item.product_code === supportingProduct?.product_code,
      ),
      gcfProducts[supportingProduct.product_code],
    );
  }

  return supportingProduct as IProduct;
};
